import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import TrivaPlansDesktop from './component/desktop_screens/Triva_Plans_Desktop';
import DirectPurchasePage from './component/desktop_screens/DirectPurchasePage';
import DirectPurchasePageMobile from './component/mobile_screens/DirectPurchasePageMobile';
import { API_URLS, IS_STAGING } from './component/api/urls';
import TrivaPlansMobile from './component/mobile_screens/Triva_Plans_Mobile';
import RecommendedPlanDetails from './component/mobile_screens/Recommended_Plan_Details';
import PaymentSuccessPage from './component/mobile_screens/Payment_Success_Page_Mobile';
import { ToastProvider } from './component/ui/Toastcontext';
import { AuthStorage } from './component/desktop_screens/auth/AuthStorage';

// Type definitions
interface ApiResponse {
  user_id?: number;
  [key: string]: any;
}

type PlanType = 'lite' | 'spark' | 'ignite' | 'thrive' | 'premium';

interface PaymentDetails {
  orderId: string;
  paymentId: string;
  amount: number;
  transactionDate: string;
  invoiceNumber?: string;
  userPaymentId?: number;
}

// Hook to detect device type
const useDeviceType = () => {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkDeviceType = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  return isMobile;
};

// Function to decode JWT and check expiration
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

// Function to clean token from URL template literal syntax
const cleanToken = (rawToken: string): string => {
  let token = rawToken;
  
  if (token.startsWith('${')) {
    token = token.substring(2);
  }
  if (token.endsWith('}')) {
    token = token.slice(0, -1);
  }
  
  return token;
};

// Function to extract user ID from token
const getUserIdFromToken = async (token: string): Promise<ApiResponse> => {
  try {
    const decoded = decodeJWT(token);
    if (decoded) {
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = decoded.exp && decoded.exp < currentTime;
    }

    const response = await fetch(API_URLS.GET_USER_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token
      })
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Could not read error response body
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

// Utility function to make authenticated API calls
export const makeAuthenticatedRequest = async (
  url: string, 
  options: RequestInit = {}, 
  token: string | null
): Promise<Response> => {
  if (!token) {
    throw new Error('No authentication token available');
  }

  const authenticatedOptions: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  };

  return fetch(url, authenticatedOptions);
};

// Shimmer skeleton that mirrors the real plan-list layout (mobile & desktop)
const DeeplinkShimmer: React.FC = () => {
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    return (
      <div className="min-h-screen bg-white px-[24px] mt-[32px]">
        {/* Header */}
        <div className="py-4">
          <div className="shimmer h-5 w-28 rounded-lg" />
        </div>

        {/* Section label */}
        <div className="shimmer h-4 w-36 rounded mb-5" />

        {/* Plan cards × 4 */}
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-2xl border border-gray-100 p-4"
              style={{ boxShadow: '0px 0px 6px 0px #0837370F' }}
            >
              <div className="flex items-start mb-3">
                <div className="shimmer w-10 h-10 rounded-lg mr-3 flex-shrink-0" />
                <div className="flex-1 space-y-1.5 pt-0.5">
                  <div className="shimmer h-4 w-28 rounded" />
                  <div className="shimmer h-3 w-44 rounded" />
                </div>
              </div>
              <div className="shimmer h-3 w-24 rounded mb-3" />
              <div className="shimmer h-11 w-full rounded-xl" />
            </div>
          ))}

          {/* Signature / premium card */}
          <div
            className="rounded-2xl border border-gray-100 p-5 mt-8"
            style={{ boxShadow: '0px 0px 6px 0px #0837370F' }}
          >
            <div className="shimmer h-6 w-20 rounded-full mb-3" />
            <div className="shimmer h-5 w-48 rounded mb-2" />
            <div className="shimmer h-3 w-56 rounded mb-5" />
            <div className="shimmer h-11 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Desktop shimmer
  return (
    <div className="min-h-screen bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="w-full px-24 py-4 mt-4">
        <div className="shimmer w-[120px] h-9 rounded" />
      </div>

      {/* Two-column layout */}
      <div className="flex-1 flex items-center justify-center px-4 3xl:px-24 py-4">
        <div className="w-full flex gap-0 3xl:gap-12 items-start justify-center">

          {/* Left – illustration placeholder */}
          <div className="w-[40%] 2xl:w-1/2 relative min-h-[600px] flex items-center justify-center">
            <div className="shimmer w-[480px] h-[500px] rounded-3xl" />
          </div>

          {/* Right – plan cards */}
          <div className="w-[60%] 2xl:w-1/2 pr-8">
            <div className="shimmer h-5 w-40 rounded mb-5" />

            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-2xl border border-gray-100 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="shimmer w-10 h-10 rounded-lg mr-4 flex-shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="shimmer h-4 w-32 rounded" />
                        <div className="shimmer h-3 w-52 rounded" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <div className="shimmer h-8 w-20 rounded" />
                      <div className="shimmer w-6 h-6 rounded" />
                    </div>
                  </div>
                </div>
              ))}

              <div className="border-[0.5px] border-[#ebebeb] my-2" />

              {/* Signature plan card */}
              <div className="rounded-2xl border border-gray-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="shimmer w-10 h-10 rounded-lg mr-4 flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="shimmer h-4 w-40 rounded" />
                      <div className="shimmer h-3 w-56 rounded" />
                    </div>
                  </div>
                  <div className="shimmer w-6 h-6 rounded ml-4" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <footer className="w-full py-4 flex justify-center">
        <div className="shimmer h-3 w-64 rounded" />
      </footer>
    </div>
  );
};

// Extract plan name from redirect_to like "/#plan-details/lite"
const extractPlanFromRedirectTo = (redirectTo: string | undefined): string | null => {
  if (!redirectTo) return null;
  const match = redirectTo.match(/plan-details\/([^/?#]+)/);
  return match ? match[1] : null;
};

// DeeplinkHandler: verifies magic-link session, stores auth, then redirects to "/"
const DeeplinkHandler: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleDeeplink = async () => {
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        navigate('/', { replace: true });
        return;
      }

      try {
        const response = await fetch(API_URLS.VERIFY_DEEPLINK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await response.json();

        if (data.success === '1' && data.access_token) {
          // Full success: store auth + plan
          AuthStorage.setAuthData(
            data.access_token,
            data.refresh_token,
            data.user_id,
            {
              user_id: data.user_id,
              user_fname: data.user_fname || '',
              user_lname: '',
              user_mobileno: '',
              otp: '',
              goal: '',
              gender: '',
              age: 0,
              city: '',
              state: '',
              dob: '',
              height_parameter: '',
              height_value: 0,
              weight_parameter: '',
              weight_value: 0,
              target_weight: 0,
              target_weight_parameter: '',
              food_preference: '',
              progress: '',
              medical_conditions: '',
              last_status: null,
              profile_url: '',
            }
          );

          sessionStorage.setItem('triva_auth_token', data.access_token);
          sessionStorage.setItem('triva_is_deeplink_user', 'true');

          // Plan comes from API response; fall back to parsing redirect_to
          const plan = data.plan || extractPlanFromRedirectTo(data.redirect_to);
          // Store in sessionStorage as fallback AND pass via router state for iOS reliability
          if (plan) {
            sessionStorage.setItem('triva_deeplink_plan', plan);
          }
          navigate('/', { replace: true, state: { deeplinkPlan: plan ?? null } });
          return;
        }
      } catch {
        // Network/parse error — navigate home without plan
      }

      navigate('/', { replace: true });
    };

    handleDeeplink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <DeeplinkShimmer />;
};

// Main App Context Component
const AppContent: React.FC = () => {
  const isMobile = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [userId, setUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  // Router state (set by DeeplinkHandler) is synchronously available on iOS and Android.
  // Fall back to sessionStorage for any edge case where state is missing.
  const deeplinkPlan: string | null =
    (location.state as { deeplinkPlan?: string } | null)?.deeplinkPlan
    ?? sessionStorage.getItem('triva_deeplink_plan')
    ?? null;
  const isDeeplinkUser = sessionStorage.getItem('triva_is_deeplink_user') === 'true';

  // Helper function to get token from URL or sessionStorage
  const getStoredToken = (): string | null => {
    // First try to get from URL
    const urlToken = searchParams.get('token');
    if (urlToken) {
      return urlToken;
    }
    
    // Then try sessionStorage
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('triva_auth_token');
    }
    
    return null;
  };

  // Helper function to store token
  const storeToken = (token: string) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('triva_auth_token', token);
    }
  };

  // Helper function to clear stored token
  const clearStoredToken = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('triva_auth_token');
    }
  };

  // Navigation handlers
  const handlePlanSelect = (planType: PlanType) => {
    const token = getStoredToken();
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    navigate(`/plan/${planType}${tokenQuery}`);
  };

  const handleBackToPlans = () => {
    // Preserve token in URL when navigating back
    const token = getStoredToken();
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    navigate(`/${tokenQuery}`);
  };

  const openApp = () => {
    const isStaging = IS_STAGING;
    const scheme = isStaging ? 'triva-test' : 'triva';
    const pkg    = 'com.triva.app';

    const ua = navigator.userAgent;
    if (/Android/i.test(ua)) {
      const fallback = encodeURIComponent(
        `https://play.google.com/store/apps/details?id=${pkg}`
      );
      window.location.href =
        `intent://home#Intent;scheme=${scheme};package=${pkg};S.browser_fallback_url=${fallback};end`;
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      const now = Date.now();
      window.location.href = `${scheme}://home`;
      setTimeout(() => {
        if (Date.now() - now < 2000) {
          window.location.href =
            `https://play.google.com/store/apps/details?id=${pkg}`;
        }
      }, 1500);
    } else {
      handleBackToPlans();
    }
  };

  // Payment success handler
  const handlePaymentSuccess = (
    planType: PlanType,
    duration: number,
    paymentData: PaymentDetails
  ) => {
    // Preserve token in URL when navigating
    const token = getStoredToken();
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    
    // Navigate with state to pass payment details and preserve token
    navigate(`/payment-success${tokenQuery}`, {
      state: {
        planType,
        duration,
        paymentDetails: paymentData
      }
    });
    
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  // Function to make authenticated API calls
  const makeAuthenticatedApiCall = async (
    url: string, 
    options: RequestInit = {}
  ): Promise<Response> => {
    return makeAuthenticatedRequest(url, options, authToken);
  };

  // Token extraction and user ID retrieval
  useEffect(() => {
    const extractTokenAndGetUserId = async () => {
      try {
        const currentIsMobile = window.innerWidth < 768;
        
        // If desktop, skip token validation and proceed directly
        if (!currentIsMobile) {
          setUserId(null);
          setAuthToken(null);
          setLoading(false);
          return;
        }

        // Mobile-only token validation logic
        
        // Extract token from URL parameters or sessionStorage
        let rawToken = getStoredToken();
        
        if (rawToken) {
          // Clean the token
          const cleanedToken = cleanToken(rawToken);
          
          // Store the cleaned token immediately in state and sessionStorage
          setAuthToken(cleanedToken);
          storeToken(cleanedToken);

          // Remove ?token= from the URL silently so Razorpay's session fingerprint
          // sees a clean URL (same as OTP users). replaceState does not fire popstate
          // so React Router's searchParams state stays unchanged — no effect re-run.
          if (searchParams.get('token')) {
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('token');
            window.history.replaceState(window.history.state, '', cleanUrl.pathname + (cleanUrl.search || ''));
          }
          
          // Decode and check token
          const decoded = decodeJWT(cleanedToken);
          if (decoded) {
            const currentTime = Math.floor(Date.now() / 1000);
            const isExpired = decoded.exp && decoded.exp < currentTime;
            
            // If token is expired but we have user_id, use it directly
            if (isExpired && decoded.user_id) {
              setUserId(decoded.user_id);
              setLoading(false);
              return;
            }
          }
          
          // Call API to get user ID from token only if token is not expired
          try {
            const userData = await getUserIdFromToken(cleanedToken);

            const extractedUserId = typeof userData === 'number' ? userData : userData.user_id;
            setUserId(extractedUserId || null);

            // Populate AuthStorage so access-token users behave identically to OTP users
            if (extractedUserId) {
              AuthStorage.setAuthData(cleanedToken, '', extractedUserId, null);
            }
          } catch (apiError) {
            // Fallback to decoded user ID if API fails
            if (decoded && decoded.user_id) {
              setUserId(decoded.user_id);
              AuthStorage.setAuthData(cleanedToken, '', decoded.user_id, null);
            } else {
              // ✅ FIX: Don't set error, just continue without userId
              console.warn('Failed to extract user ID from token, continuing without authentication');
              setUserId(null);
              setAuthToken(null);
            }
          }
        } else {
          // ✅ FIX: No token is provided - this is OK, continue without authentication
          // User will go through login flow when they try to select a plan
          console.log('No token provided - user will authenticate when selecting a plan');
          setUserId(null);
          setAuthToken(null);
        }
      } catch (err) {
        // ✅ FIX: Don't set error, just continue without authentication
        console.warn('Token validation failed, continuing without authentication:', err);
        setUserId(null);
        setAuthToken(null);
      } finally {
        setLoading(false);
      }
    };

    extractTokenAndGetUserId();
  }, [searchParams, location.pathname]);

  // Clean up the sessionStorage key once the plan is consumed
  useEffect(() => {
    sessionStorage.removeItem('triva_deeplink_plan');
  }, []);

  // Show loading state while fetching user ID (only for mobile)
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        Loading...
      </div>
    );
  }

  // ✅ FIX: Removed error state display - we allow access without token
  // The login flow will handle authentication when needed

  return (
    <Routes>
      {/* Main Plans Route */}
      <Route
        path="/"
        element={
          isMobile ? (
            <TrivaPlansMobile
              userId={userId}
              authToken={authToken}
              makeAuthenticatedApiCall={makeAuthenticatedApiCall}
              onPaymentSuccess={handlePaymentSuccess}
              deeplinkPlan={deeplinkPlan}
            />
          ) : (
            <TrivaPlansDesktop
              userId={userId}
              deeplinkPlan={deeplinkPlan}
            />
          )
        }
      />

      {/* Deeplink entry point: /start?session_id=...&plan=... */}
      <Route path="/start" element={<DeeplinkHandler />} />

      {/* Direct Purchase Route: /purchase/plan/:planName/:encodedId */}
      <Route
        path="/purchase/plan/:planName/:encodedId"
        element={isMobile ? <MobilePurchasePlanRoute /> : <PurchasePlanRoute />}
      />

      {/* Payment Success Route */}
      <Route
        path="/payment-success"
        element={<PaymentSuccessWrapper
          userId={userId}
          onNavigateHome={isDeeplinkUser ? openApp : handleBackToPlans}
        />}
      />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Encode/decode helpers for plan ID obfuscation
export const encodePlanId = (id: number): string => {
  return btoa("triva_" + id).replace(/=/g, "");
};

const decodePlanId = (encoded: string): number => {
  try {
    const padded = encoded + "=".repeat((4 - (encoded.length % 4)) % 4);
    const decoded = atob(padded);
    const num = parseInt(decoded.replace("triva_", ""), 10);
    return isNaN(num) ? NaN : num;
  } catch {
    return NaN;
  }
};

// Mobile Purchase Plan Route
const MobilePurchasePlanRoute: React.FC = () => {
  const { encodedId } = useParams<{ planName: string; encodedId: string }>();
  const parsedId = encodedId ? decodePlanId(encodedId) : NaN;

  if (isNaN(parsedId)) return <Navigate to="/" replace />;

  return <DirectPurchasePageMobile standardDetailsId={parsedId} />;
};

// Desktop Purchase Plan Route: handles /purchase/plan/:planName/:encodedId URLs
const PurchasePlanRoute: React.FC = () => {
  const { encodedId } = useParams<{ planName: string; encodedId: string }>();
  const parsedId = encodedId ? decodePlanId(encodedId) : NaN;

  if (isNaN(parsedId)) return <Navigate to="/" replace />;

  return <DirectPurchasePage standardDetailsId={parsedId} />;
};

// Payment Success Wrapper Component
const PaymentSuccessWrapper: React.FC<{
  userId: number | null;
  onNavigateHome: () => void;
}> = ({ userId, onNavigateHome }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { planType, duration, paymentDetails } = location.state || {};

  // Redirect to home if no payment details are found
  if (!planType || !duration || !paymentDetails) {
    return <Navigate to="/" replace />;
  }

  return (
    <PaymentSuccessPage
      userId={userId}
      planType={planType}
      selectedDuration={duration}
      paymentDetails={paymentDetails}
      onNavigateHome={onNavigateHome}
    />
  );
};

// Main App Component with Router
function App() {
  return (
    <Router>
      <ToastProvider>
        <div className="App">
          <AppContent />
        </div>
      </ToastProvider>
    </Router>
  );
}

export default App;